import * as React from 'react';
import autobind from 'autobind-decorator';
import { resolve } from 'inversify-react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';

import { AutoConectTransferView } from './auto-connect-tranfer.view';
import { ProductProviderDataService } from 'src/app/products/product-providers/product-provider.data';
import { CalculatePriceService } from 'src/app/bookings/shared/calculate-prices.service';
import { FormatService } from 'src/core/services/format.service';
import { ObjectUtils } from 'src/core/utils/object';

interface AutoConectTranferProps {
	setBusy(isBusy: boolean);
	quote: QuoteItem;
	addProduct: (products: ResultSelection[], search: Search) => Promise<QuoteEntryItem[]>;
	showModal: boolean;
	entries: QuoteEntryItem[];
	paxs: QuotePaxItem[];
	closeAutocconetDialog: (ev?: React.MouseEvent<HTMLButtonElement>) => void
	formatService: FormatService
}

@observer
export class AutoConectTransferController extends React.Component<AutoConectTranferProps, {}> {
	@resolve
	private calculatePriceService: CalculatePriceService;

	@observable
	private dataService: ProductProviderDataService;

	private headerMsg: string;

	private apiNames: Api[];

	@observable
	private autoconectProduct: AutoConnectTranferItem[] = [];

	public componentDidMount() {
		this.loadData();
	}

	public componentWillReceiveProps(nextProps: Readonly<AutoConectTranferProps>, nextContext: any) {
		if (!ObjectUtils.equals(this.props.entries, nextProps.entries)) {
			this.makeAutoConnectProductTransfe(nextProps.entries, nextProps.paxs);
		}
	}

	public render() {
		if (this.apiNames == null || (this.apiNames != null && this.apiNames.length == 0)) {
			return null;
		}
		return (
			<AutoConectTransferView
				makeParameter={this.makeParameter}
				quote={this.props.quote}
				formatService={this.props.formatService}
				autoConnectTransferItems={this.autoconectProduct}
				headerMsg={this.headerMsg}
				apiNames={this.apiNames}
				onAceptSelectedTransfers={this.onAceptSelectedTransfers}
				onAutoConect={this.onAutoConect}
				setBusy={this.props.setBusy}
				showModal={this.props.showModal}
				closeAutocconetDialog={this.props.closeAutocconetDialog}
			/>
		);
	}

	@autobind
	private async loadData() {
		this.dataService = ProductProviderDataService.make();
		this.props.setBusy(true);
		this.dataService.addFilter('isDeleted', 'eq', false);
		this.dataService.addSorting('id', 'asc', true);
		let items = await this.dataService.load(query => query);
		this.apiNames = items
			.filter(x => x.isActiveForApiTransfer)
			.map(o => ({ apiName: o.apiName, name: o.name, isActive: o.isActiveForTransfer, isActiveForApi: o.isActiveForApiTransfer }));
		this.makeAutoConnectProductTransfe(this.props.entries, this.props.paxs);
		this.props.setBusy(false);
	}

	private getType(type: 'Accommodation' | 'CarRent' | 'General' | 'Transfer') {
		return type;
	}

	private getPaxsFrom(occupancies: Occupancy[]) {
		let paxs: PersonOccupancy[] = [];
		for (let occ of occupancies)
			paxs = paxs.concat(occ.paxs);
		return paxs;
	}

	private convertInQuoteEntryAutoConnect(entries: QuoteEntryItem[], paxs: QuotePaxItem[]) {
		const result: QuoteEntryAutoConnect[] = [];
		for (const entry of entries) {
			const occupancies = this.calculatePriceService.getOccupancy(entry, paxs);
			const paxes = this.getPaxsFrom(occupancies);
			if (entry.dynamicProperties.parentName) {
				let parent = result.find(e => e.id == entry.dynamicProperties.parentInternalId || e.id == entry.dynamicProperties.externalId);
				if (parent) {
					parent.paxs = parent.paxs.concat(paxes.map(p => p.name));
					parent.occupancies[0].paxs = parent.occupancies[0].paxs.concat(paxes);
				} else {
					result.push({
						productDisplayName: entry.dynamicProperties.parentName,
						id: entry.dynamicProperties.parentInternalId ? entry.dynamicProperties.parentInternalId : entry.dynamicProperties.externalId,
						dateTo: entry.dateTo,
						paxs: paxes.map(p => p.name),
						placeId: entry.dynamicProperties.placeId ? entry.dynamicProperties.placeId : (entry.dynamicProperties.flightLegs as FlightLegResult[])[(entry.dynamicProperties.flightLegs as FlightLegResult[]).length - 1].destinationAirport.internalId,
						occupancies: occupancies
					})
				}
			} else {
				result.push({
					productDisplayName: entry.productDisplayName,
					id: entry.id,
					dateTo: entry.dateTo,
					paxs: paxes.map(p => p.name),
					occupancies: occupancies,
					placeId: entry.dynamicProperties.placeId ? entry.dynamicProperties.placeId : (entry.dynamicProperties.flightLegs as FlightLegResult[])[(entry.dynamicProperties.flightLegs as FlightLegResult[]).length - 1].destinationAirport.internalId,
				})
			}
		}
		return result;
	}

	private makeAutoConnectProductTransfe(entries: QuoteEntryItem[], paxs: QuotePaxItem[]) {
		this.autoconectProduct = [];
		const groupEntries = this.convertInQuoteEntryAutoConnect(entries, paxs);
		for (let i = 0; i < groupEntries.length - 1; i++) {
			const from = groupEntries[i];
			const to = groupEntries[i + 1];
			if ((from.placeId) && (to.placeId)) {
				this.autoconectProduct.push({
					id: `${from.id}-${to.id}`,
					fromProductName: from.productDisplayName,
					toProductName: to.productDisplayName,
					date: from.dateTo,
					paxs: from.paxs,
					occupancies: from.occupancies,
					fromPlaceId: from.placeId,
					toPlaceId: to.placeId,
					fromEntryId: from.id,
					toEntryId: to.id,
					productCategoryId: null,
					productServiceTypeId: null,
				})
			}
		}
	}

	@autobind
	private makeParameter(apiNames: Api[], autoconectProduct: AutoConnectTranferItem[]) {
		const parameters = [];
		if (autoconectProduct.length >= 1) {
			this.headerMsg = "Transfer will be searched to connect the products: ";
			for (const entry of autoconectProduct) {
				this.headerMsg = this.headerMsg + entry.fromProductName + " , ";
				const transferParam = {
					title: `From ${entry.fromProductName} to ${entry.toProductName}`,
					fromEntryId: entry.fromEntryId,
					toEntryId: entry.toEntryId,
					productCategoryId: entry.productCategoryId,
					productServiceTypeId: entry.productServiceTypeId,
					transferParam: {
						apiNames: apiNames,
						isActive: true,
						searchInCache: false,
						startDate: entry.date,
						endDate: entry.date,// se esta sumiendo que no hay huecos entre as fechas, por ejemplo Nacional salgo el 1/sept llego el 1/sep a Varadero
						occupancies: entry.occupancies,
						fromPlaceId: entry.fromPlaceId,
						toPlaceId: entry.toPlaceId,
						returnJourney: false,
						type: this.getType('Transfer'),
						facets: [],
						productCategoryId: entry.productCategoryId,
						productServiceTypeId: entry.productServiceTypeId,

					}
				}
				parameters.push(transferParam);
			}
			this.headerMsg = this.headerMsg.slice(0, this.headerMsg.length - 2);
		}
		return parameters;
	}

	@autobind
	private async onAutoConect(parameters: SearchAutoConnetTransfer[]) {
		this.props.setBusy(true);

		const result = await this.calculatePriceService.searchAutoConnectTransfer(
			parameters,
			this.props.quote.marketId,
			this.props.quote.reservationDate,
			this.props.quote.languageCode,
			null
		);
		this.props.setBusy(false);
		return result;
	}

	@autobind
	private async onAceptSelectedTransfers(transfers: ResultSelection[], transferParam) {
		if (transfers && transfers.length > 0) {
			this.props.setBusy(true);
			let res = await this.props.addProduct(transfers, transferParam);
			this.props.setBusy(false);
		}
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/auto-connect-transfer/auto-connect-tranfer.controller.tsx